import React, { useEffect, useMemo } from "react";
import { get, noop } from "lodash";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { Store } from "redux";
import {
  AlertInline,
  buildColor,
  Button,
  Header,
  Link,
  Loading,
  Mtp,
  Paragraph
} from "@tvg/design-system";
import formatCurrency from "@tvg/formatter/currency";
import { getWalletBalances } from "@tvg/sh-utils/walletUtils";
import {
  betCancelRequestError,
  cleanBetCancelResult,
  closeBetCancelModal,
  loadingBetCancelModalOn,
  openWagerCancellationRulesModal
} from "@tvg/sh-lib-my-bets/redux/actions";
import CancelLimitsQuery from "@tvg/sh-lib-my-bets/graphql/queries/CancelLimits.graphql";
import BehgOptionsCancel from "@tvg/sh-lib-my-bets/graphql/behgOptionsCancel.graph";
import { graphql } from "@apollo/client/react/hoc";
import {
  getBetCancelBet,
  getBetCancelIsLoadingRequest,
  getBetCancelRaces,
  getBetCancelRequestError,
  getBetCancelWager,
  getTotalActiveBets,
  getTotalSettledBets
} from "@tvg/sh-lib-my-bets/redux/selectors";
import sendAppMetrics from "@tvg/metrics-collector";
import { updateBalance } from "@tvg/shared-actions/UserActions";
import mediator from "@tvg/mediator";
import tvgConf from "@tvg/conf";
import PromosOnboarding from "@tvg/promos-onboarding";
import { getStoryblokPromoByStep } from "@tvg/sh-lib-promos-onboarding/redux/selectors";
import { matchPlacedWager } from "@tvg/sh-lib-promos-onboarding/utils/matchPlacedWager";
import {
  PromoStepType,
  WagerStep
} from "@tvg/sh-lib-promos-onboarding/types/promoSteps";
import { WroWager } from "@tvg/ts-types/WroWager";
import { StoryblokPlaceName } from "@tvg/sh-lib-promos-onboarding/types/promoOnboardingComponents";
import BetUtils from "@tvg/utils/betSelection";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";

import { AlertInlineVariants } from "@tvg/design-system/src/components/alertInline/types";
import { TextStyle } from "react-native";
import { getAccountNumber } from "@urp/store-selectors";

import {
  cancelBetErrorGtmEvent,
  cancelBetSubmitGtmEvent,
  cancelBetSuccessGtmEvent,
  keepBetGtmEvent
} from "../../utils/gtm/betCancel";

import type {
  GetNotificationArguments,
  HandleCancelBetArg,
  Props
} from "./types";
import cancelBet from "../../utils/service";
import {
  BetCancelContainer,
  BetCancelLoadingMask,
  BetItemWrapperBetCancel,
  ButtonsContainer,
  SeeRulesContainer
} from "./styled-components";
import { BetSummary } from "../BetSummary";
import { getBettingInfo } from "../../utils/betUtils";

export const handleCancelBet = ({
  wager,
  raceNumber,
  accountNumber,
  dispatch
}: HandleCancelBetArg) => {
  cancelBetSubmitGtmEvent({
    raceNumber: `${raceNumber}`,
    trackName: wager.trackName,
    betAmount: wager.betTotal,
    betType: get(wager, "wagerType.code", "")
  });

  dispatch(loadingBetCancelModalOn());

  return cancelBet({ wager, accountNumber })
    .then((res) => {
      sendAppMetrics("frontend-desk", accountNumber, ["cancel_bets_number"]);

      dispatch(
        closeBetCancelModal({
          result: { status: "success" }
        })
      );
      // wait until toast animation finishes
      setTimeout(() => {
        dispatch(cleanBetCancelResult());
      }, 3500);

      const newBalance = get(res, "data.userBalance", undefined);
      if (newBalance) {
        dispatch(updateBalance(newBalance));

        if (tvgConf().device === "desktop") {
          mediator.base.dispatch({
            type: "ACCOUNT_BALANCE_CHANGED",
            payload: { balance: newBalance }
          });
        }

        if (tvgConf().brand === "fdr") {
          getWalletBalances(dispatch);
        }
      }

      cancelBetSuccessGtmEvent({
        raceNumber: `${raceNumber}`,
        trackName: wager.trackName,
        betAmount: wager.betTotal,
        betType: get(wager, "wagerType.code", "")
      });
    })
    .catch((err: unknown) => {
      const errorCode = get(err, "response.data.code", "default");

      cancelBetErrorGtmEvent({
        gaEventLabel: get(err, "message", ""),
        raceNumber: `${raceNumber}`,
        trackName: wager.trackName,
        betAmount: wager.betTotal,
        betType: get(wager, "wagerType.code", ""),
        errorType: get(err, "message", "")
      });

      dispatch(betCancelRequestError({ payload: `${err}` }));

      throw errorCode;
    });
};

export const handleKeepBet = ({ dispatch }: { dispatch: Dispatch }) => {
  dispatch(closeBetCancelModal({ result: { status: "aborted" } }));
  dispatch(cleanBetCancelResult());
  keepBetGtmEvent();
};

export const getNotificationArguments = ({
  limits,
  serviceError
}: GetNotificationArguments) => {
  let notificationArguments = {
    title: "Please confirm that you want to cancel this bet.",
    message: `You can only cancel ${limits.remainingDayCancelledCount} bets per day`,
    variant: "informational",
    isLinkBellow: true,
    hasLink: true
  };
  if (!serviceError) {
    if (
      limits.remainingDayCancelledCount === 0 ||
      limits.remainingDayCancelledAmount === 0
    ) {
      notificationArguments = {
        title: "Unable to cancel bet",
        message: "You have reached your cancelation limit",
        variant: "error",
        isLinkBellow: false,
        hasLink: false
      };
    } else if (limits.currentDayCancelledCount > 0) {
      notificationArguments = {
        title: `You can cancel ${limits.remainingDayCancelledCount} more bets today`,
        message: `You can currently cancel bets up to ${formatCurrency(
          limits.remainingDayCancelledAmount
        )}`,
        variant: "informational",
        isLinkBellow: true,
        hasLink: true
      };
    }
  } else {
    notificationArguments = {
      title: "Something went wrong...",
      message: "Close and try again later",
      variant: "error",
      isLinkBellow: false,
      hasLink: false
    };
  }

  return notificationArguments;
};

export const formatWager = (wager: WroWager) => [{ wagers: [wager] }];

export const BetCancel = (props: Props) => {
  const {
    bet,
    wager,
    accountNumber,
    isLoadingLimitsRequest, // from bet cancel request
    isLoadingBetCancelRequest, // from graph limits request
    dispatch,
    limits,
    queryError,
    betCancelRequestErrorMessage,
    selectedTab,
    selectedSettledTab,
    races,
    totalActiveBets,
    totalSettledBets,
    activeBets,
    settledBets,
    isDesktop,
    promosOnboardingStep
  } = props;
  const isLoading = isLoadingLimitsRequest || isLoadingBetCancelRequest;

  const notificationArguments = getNotificationArguments({
    limits,
    serviceError: queryError || betCancelRequestErrorMessage
  });
  const hasError =
    betCancelRequestErrorMessage ||
    queryError ||
    limits.remainingDayCancelledAmount <= 0 ||
    limits.remainingDayCancelledCount <= 0;

  useEffect(() => {
    if (!isLoading) {
      mediator.base.dispatch({
        type: "MYBETS_USER_SAW_WARNING_MESSAGE",
        payload: {
          selectedTab,
          selectedSettledTab,
          message: notificationArguments.title,
          activeBets: isDesktop ? activeBets : totalActiveBets,
          settledBets: isDesktop ? settledBets : totalSettledBets
        }
      });
    }
  }, [isLoading]);

  const onShowCancelRules = () => {
    mediator.base.dispatch({
      type: "MYBETS_USER_CLICKS_SEE_RULES_ON_CANCELLED_BET",
      payload: {
        selectedTab,
        selectedSettledTab,
        activeBets: isDesktop ? activeBets : totalActiveBets,
        settledBets: isDesktop ? settledBets : totalSettledBets
      }
    });

    dispatch(closeBetCancelModal());
    setTimeout(() => {
      dispatch(openWagerCancellationRulesModal({ hasBack: true }));
    }, 200);
  };

  const promoOnboardingComponent = useMemo(() => {
    const renderPlace = "cancel_bet" as StoryblokPlaceName;
    const isValidWager = matchPlacedWager(
      promosOnboardingStep as WagerStep,
      wager
    );

    if (!isValidWager) {
      return null;
    }
    return (
      <PromosOnboarding placeToRender={renderPlace} isShown={isValidWager} />
    );
  }, [wager, accountNumber, promosOnboardingStep]);

  const mainWagerDetails = get(bet, "value", "||0").split("|");
  const [raceDate, trackCode, raceNumber] = mainWagerDetails;
  const currentRace = races.find(
    (race) =>
      race.id === `${trackCode}-${raceNumber}` && race.raceDate === raceDate
  );

  const typeFromRace = useMemo(
    () =>
      BetUtils.getBetTypeFromRace(
        (currentRace || {}) as RaceProgram,
        wager.wagerType.id,
        true
      ),
    [currentRace?.wagerTypes]
  );

  const selections = wager.selections.selection.map((entry) =>
    entry.bettingInterests.map((bi) => `${bi.biNumber}`)
  );

  const { fullSelections, numberOfWagerableRunners } = getBettingInfo(
    selections,
    wager.wagerAmount,
    currentRace as unknown as RaceProgram,
    races as unknown as RaceProgram[],
    typeFromRace
  );

  const scratchedSelections = fullSelections.map((entry) =>
    entry.filter((value) => value.scratched).map((value) => value.number)
  );

  return (
    <>
      <BetItemWrapperBetCancel data-qa-label="bet-cancel-item-wrapper">
        {promoOnboardingComponent ||
          (!isLoading && (
            <AlertInline
              qaLabel="cancel-bet-alert"
              type="fixed"
              message={notificationArguments.message}
              title={notificationArguments.title}
              variant={notificationArguments.variant as AlertInlineVariants}
              actionLink={
                notificationArguments.hasLink && (
                  <SeeRulesContainer>
                    <>
                      <Link
                        qaLabel="bet-cancel-rules-link"
                        onPress={() => onShowCancelRules()}
                        style={
                          {
                            textDecoration: "underline",
                            color: buildColor("blue_accent", "700")
                          } as TextStyle
                        }
                      >
                        See rules
                      </Link>
                      <Paragraph
                        qaLabel="bet-cancel-rules-text"
                        fontFamily="medium"
                        color={buildColor("blue_accent", "700")}
                      >
                        &nbsp;for more details
                      </Paragraph>
                    </>
                  </SeeRulesContainer>
                )
              }
            />
          ))}
        <BetCancelContainer>
          <Mtp
            isCollapsed
            mtp={+(currentRace?.mtp || 0)}
            status={currentRace?.status.code || RaceStatusEnum.RACE_OFFICIAL}
            postTime={currentRace?.postTime as string}
            hasFullText
            qaLabel="confirm-bet-modal-mtp"
          />
          <Header
            tag="h3"
            fontFamily="bold"
            color="grey.900"
            qaLabel="confirm-bet-modal-race-name"
            mb="space-1"
          >
            {`${wager.trackName} R${raceNumber}`}
          </Header>
          <Paragraph
            color="grey.800"
            qaLabel="confirm-bet-modal-bet-description"
            mb="space-4"
          >{`${wager ? 1 : 0} Bet • ${formatCurrency(
            wager.betTotal || 0
          )} Staked`}</Paragraph>
          <BetSummary
            numWagerableRunners={numberOfWagerableRunners}
            betSelectionsNumbers={selections}
            betAmount={wager.wagerAmount}
            fullSelections={fullSelections}
            betTotal={`${wager.betTotal}`}
            isSpecialGroup={false}
            typeFromRace={typeFromRace}
            probables={get(currentRace, "probables", [])}
            raceNumber={raceNumber}
            scratches={scratchedSelections}
            allBetsPlaced={false}
            betGroupStatus={{}}
            specialGroupBetsWithOutScratches={{}}
          />
        </BetCancelContainer>
      </BetItemWrapperBetCancel>
      <ButtonsContainer>
        {hasError ? null : (
          <Button
            variant="danger"
            isLoading={isLoading}
            qaLabel="betCancelButtonConfirm"
            onPress={
              isLoading
                ? noop
                : () =>
                    handleCancelBet({
                      wager,
                      raceNumber: +raceNumber,
                      accountNumber,
                      dispatch
                    })
            }
            isStretched
          >
            {isLoading ? <Loading /> : "Cancel Bet"}
          </Button>
        )}
        <Button
          mt="8"
          qaLabel="betCancelButtonKeep"
          variant="secondary"
          onPress={() => handleKeepBet({ dispatch })}
          isStretched
        >
          {hasError ? "Close" : "Keep Bet"}
        </Button>
      </ButtonsContainer>
      {isLoading && (
        <BetCancelLoadingMask data-qa-label="betCancelLoadingOverlay" />
      )}
    </>
  );
};

BetCancel.defaultProps = {
  wager: {},
  raceNumber: 1,
  accountNumber: "",
  isLoadingLimitsRequest: false,
  isLoadingBetCancelRequest: false,
  dispatch: noop,
  limits: {},
  queryError: null,
  betCancelRequestErrorMessage: "",
  selectedTab: "ACTIVE",
  selectedSettledTab: "TODAY",
  races: [],
  totalActiveBets: 0,
  totalSettledBets: 0,
  promosOnboardingStep: null
};

export default connect(
  (store: Store) => ({
    bet: getBetCancelBet(store),
    races: getBetCancelRaces(store),
    wager: getBetCancelWager(store),
    accountNumber: getAccountNumber(store),
    isLoadingBetCancelRequest: getBetCancelIsLoadingRequest(store),
    betCancelRequestErrorMessage: getBetCancelRequestError(store),
    selectedTab: get(store, "myBetsStandalone.selectedTab"),
    selectedSettledTab: get(store, "myBetsStandalone.selectedSettledTab"),
    totalActiveBets: getTotalActiveBets(store),
    totalSettledBets: getTotalSettledBets(store),
    promosOnboardingStep: getStoryblokPromoByStep(
      store,
      "WAGER_STEP" as PromoStepType
    )
  }),
  (dispatch: Dispatch) => ({ dispatch })
  // @ts-ignore
)(graphql(CancelLimitsQuery, BehgOptionsCancel)(BetCancel));
